<script>
  import { getContext, afterUpdate, onMount } from "svelte";
  import OperationTableControl from "./OperationTableControl.svelte";
  import { usePageContext } from "../../../components/pages/pageContext.js";
  import { translate } from "../../../i18n/i18next.js";
  import {
    getOperationTableConfiguration
  } from "../../../utils/configurationStorage/views/OperationTableConfigurationStorage.js";
  import { applyTransactionsToOperations } from "../../../utils/mergeData.js";
  import GeneralButton from "../../../components/button/GeneralButton.svelte";
  import Loading from "../../../components/pages/Loading.svelte";
  import { getFixedProperty } from "../../../utils/getData.js";
  import { postStartEndData } from "../../../utils/putData.js";
  import { useTimezone } from "../../../hooks/useTimezone.js";
  import { useProject, useProjectQuery } from "../../../hooks/useProject.js";
  import { getTimeStringSec } from "../../../utils/time.js";
  import * as Papa from "papaparse"
  import { GetStatus, GetStatusText } from "../../../components/ganttchart/GanttHelper";
  import Table from "../../../components/table/Table.svelte";
  import TableHeader from "../../../components/table/TableHeader.svelte";
  import TableHeaderCell from "../../../components/table/TableHeaderCell.svelte";
  import TableBody from "../../../components/table/TableBody.svelte";
  import TableRow from "../../../components/table/TableRow.svelte";
  import TableCell from "../../../components/table/TableCell.svelte";
  import { constructEnumPropertiesDict, getAliasOrDisplayStringForEnumValue } from "./utils.js";
  import dayjs from "dayjs";
  import ResultEdit from "../../../components/ganttchart/ResultEdit.svelte";
  import { getNotificationsContext } from "svelte-notifications"
  import { formatDateTime, formatDateTimeFromDate } from "../../../utils/dateFormatter";
  import { writable } from "svelte/store";

  const { addNotification } = getNotificationsContext();

  /**
   * @type {string}
   */
  export let projectId
  export let user
  export let styles
  export let styleIndex = 1
  export let operationsData
  export let propertyDefsData = null
  export let transactions
  export let reloadTransactions
  export let isInputAllowed

  const {getOffset} = useTimezone()
  const project = useProject(projectId)

  const enableResultEdit = writable(true);

  const projectQuery = useProjectQuery(projectId, {
    onSuccess: data => ((settings) => {
      enableResultEdit.set(settings.isOperationTableResultEditEnabled === undefined ? true : settings.isOperationTableResultEditEnabled);
    })(data.settings),
  })
  // please keep the line below to force the above query to run
  // as I am yet to know how to do so on svelte-query declaratively...
  console.log($projectQuery)

  let showResultEdit = false;
  let operationToEditResult = null;

  const editResultOnClick = async (operation) => {
    showResultEdit = true;
    operationToEditResult = operation;
  }

  const editResultOnSubmit = async(newStartDateTimeString, newEndDateTimeString, newStatus, newQty, newProgress, newObtainTime) => {
    const operationCode = getFixedProperty(operationToEditResult, "OperationCode");
    const payload = JSON.stringify({
      [operationCode]: {
        Work_Status: newStatus,
        Work_ResultStartTime: newStartDateTimeString,
        Work_ResultEndTime: newEndDateTimeString,
        Work_ResultQty: newQty,
        Work_ResultProgress: newProgress,
        Work_ResultObtainTime: newObtainTime
      },
    });

    // Stupid but quite simple way to avoid the bug that when clear result start/end time in the result edit dialog, 
    // the result-time-text that replaced the former button will still exist
    [newStartDateTimeString, newEndDateTimeString].forEach((dateTime, index) => {
      if (!dateTime) {
        const element = document.getElementById(`${operationCode}-${index === 0 ? "start" : "end"}`);
        if (element) element.outerHTML = "";
      }
    });

    try {
      await postStartEndData(projectId, payload);
      addNotification({
        text: translate("generic.saved"),
        position: "top-right",
        removeAfter: 4000,
        type: "success",
      });
      showResultEdit = false;
      setTimeout(() => {
        reloadTransactions()
      }, 1000);
    } catch (e) {
      if (e instanceof FetchError) {
        handleNonBlockingFetchErrors(e.error);
      }
    }
  }

  $: availableStyles = styles?.map(style => ({name: style[0], index: style[1]})) ?? []

  const { setHeaderProps } = usePageContext();
  $: setHeaderProps({ title: translate("mySchedule.views.operationTable.name") });
  $: setHeaderProps({ uploadTime: operationsData?.[4]});

  const { handleNonBlockingFetchErrors } = getContext("fetchErrorHandler");

  // page internal states
  let itemPerPage = null
  let currentPageIndex = null
  let totalNumberOfPages = 1
  let usingCompactStyle = false
  async function loadSavedSettings(userId, projectId) {
    // load settings from localStorage and apply them
    const savedSettings = await getOperationTableConfiguration(userId, projectId);
    // apply itemsPerPage
    if (savedSettings.itemPerPage) {
      itemPerPage = savedSettings.itemPerPage
    }
    if (savedSettings.usingCompactMode) {
      usingCompactStyle = savedSettings.usingCompactMode
    }
    // apply style
    if (savedSettings.styleName) {
      const found = availableStyles.find(style => style.name === savedSettings.styleName)
      if (found) styleIndex = found.index
    }
  }

  // operations
  $: transactionsAppliedAllData = applyTransactionsToOperations(operationsData?.[1], transactions)
  $: totalNumberOfPages = Math.floor(transactionsAppliedAllData.length / itemPerPage) + 1
  $: displayedOperations = filterOperationsToBeDisplayed(transactionsAppliedAllData, itemPerPage, currentPageIndex)
  function filterOperationsToBeDisplayed(allOperations, itemPerPage, currentPageIndex) {
    return transactionsAppliedAllData.filter((operation, index) => {
      return currentPageIndex * itemPerPage <= index && index <= (currentPageIndex + 1) * itemPerPage - 1
    })
  }
  $: displayedHeaderKeys = operationsData?.[3]?.filter(key => key !== "MyScheReservedFixedProperty") ?? []
  $: headerDict = Object.fromEntries(operationsData?.[3]?.map((header, i) => [header, operationsData?.[2][i]]) ?? [])
  $: enumPropertiesDict = constructEnumPropertiesDict(propertyDefsData)

  // transactions: start button click
  async function onStartEndButtonClick(event, operation, transactionType) {
    // if project data is not ready yet, reject submission
    if (!$project?.timezone) return
    const currentDateTimeString = getTimeStringSec(new Date(), $project.timezone, $getOffset)

    // disable buttons while the update is in progress
    // if the update is to end an operation and the view is set not to display completed operations, disable all the buttons
    //   this is to avoid update mistakes (a feature requested by a PoC user)
    // otherwise disable only the pressed button
    const disabledButtons = [];
    if (transactionType === "end") {
      // 全てのボタンを押せなくする
      const startEndButtons = document.getElementsByClassName("button-start-end");
      for (const button of startEndButtons) {
        button.disabled = "disabled";
        disabledButtons.push(button);
      }
    } else {
      event.target.disabled = true;
      disabledButtons.push(event.target);
    }

    // prepare the payload
    const operationCode = getFixedProperty(operation, "OperationCode");
    const payload = JSON.stringify({
      [operationCode]: {
        Work_Status: transactionType === "start" ? "T" : "B",
        Work_ResultStartTime:
          transactionType === "start" ? currentDateTimeString : operation["Work_ResultStartTime"] ? dayjs(operation["Work_ResultStartTime"]).format("YYYY/MM/DD HH:mm:ss") : undefined,
        Work_ResultEndTime:
          transactionType === "end" ? currentDateTimeString : undefined,
        Work_ResultMainRes: "",
        Work_ResultQty: null,
        Work_ResultProgress: null,
        Work_ResultObtainTime: null
      },
    });
    try {
      await postStartEndData(projectId, payload);

      // To avoid duplicate time texts, one of them will be hidden automatically by the css.
      // Look for ":global(div.result-time-text + div.result-time-text)" in the style tag for the actual css.
      const divElement = document.createElement("div");
      divElement.textContent = formatDateTime(currentDateTimeString);
      divElement.style.color = "#249E8E";
      divElement.className = "result-time-text"
      divElement.id = `${operationCode}-${transactionType}`; // This id will be only be set for this generated result-time-text element
      event.target.replaceWith(divElement);

      setTimeout(() => {
        disabledButtons.forEach((button) => (button.disabled = false));
        reloadTransactions()
      }, 1000);
    } catch (e) {
      if (e instanceof FetchError) {
        handleNonBlockingFetchErrors(e.error);
      }
      disabledButtons.forEach((button) => (button.disabled = false));
    }
  }

  function downloadOperationTableAsCsv() {
    const csvHeader = Papa.unparse([displayedHeaderKeys.map(key => headerDict[key])], {header: false})
    const csvBody = Papa.unparse(transactionsAppliedAllData, {columns: displayedHeaderKeys, header: false})

    // append the BOM to the payload to make it compatible with Microsoft Excel
    const bom  = new Uint8Array([0xEF, 0xBB, 0xBF]);
    const blob = new Blob([bom, csvHeader + "\n" + csvBody])
    const objectUrl = URL.createObjectURL(blob)
    const aElement = document.createElement("a")
    aElement.href = objectUrl
    aElement.download = "operations.csv"
    aElement.click()
  }

  function loadSearchParams() {
    const searchParams = new URLSearchParams(window.location.search);
    if (searchParams.get("style")) {
      const specifiedStyleName = searchParams.get("style");
      // check if the specified style name is valid
      const found = styles.find((s) => s[0] === specifiedStyleName);
      if (found) styleIndex = found[1];
    }
  }

  onMount(async () => {
    await loadSavedSettings(user?.id, projectId)
  })

  afterUpdate(async () => {
    loadSearchParams();
  })
</script>

<div class="content-root">
  <OperationTableControl
    bind:itemPerPage={itemPerPage}
    totalPageNumber={totalNumberOfPages}
    bind:styleIndex={styleIndex}
    styles={availableStyles}
    bind:usingCompactStyle={usingCompactStyle}
    bind:currentIndex={currentPageIndex}
    userId={user.id}
    projectId={projectId}
    onClickExportCsv={downloadOperationTableAsCsv}
    {enableResultEdit}
  />
  <div class="table-wrapper">
    <!-- Main Table -->
    {#if transactionsAppliedAllData}
      {#if transactionsAppliedAllData.length > 0}
        <Table
          type={usingCompactStyle ? "compact" : "wide"}
        >
          <TableHeader>
            {#each displayedHeaderKeys as key}
              <TableHeaderCell>{headerDict[key]}</TableHeaderCell>
            {/each}
            {#if $enableResultEdit && isInputAllowed}
              <TableHeaderCell stickToRight={true}>{translate("generic.editResultShort")}</TableHeaderCell>
            {/if}
          </TableHeader>
          <TableBody>
            {#each displayedOperations as operation, i}
              <TableRow id={`row-${i}`}>
                {#each displayedHeaderKeys as headerKey, j}
                  {#if headerKey === "Work_ResultStartTime"}
                    <TableCell>
                      {#if operation["Work_ResultStartTime"] !== "" || getFixedProperty(operation, "Status") === "B"}
                        <div style="color:#249E8E;" class="result-time-text">
                          {formatDateTimeFromDate(new Date(operation["Work_ResultStartTime"]))}
                        </div>
                      {:else}
                        <GeneralButton
                          buttonDisabled={!isInputAllowed}
                          buttonFunction={(e) =>
                            onStartEndButtonClick(e, operation, "start")}
                          buttonText={translate("generic.startShort")}
                          buttonExtraStyle="padding: 0; margin: 0; width: 100%; min-height: 50px"
                        />
                      {/if}
                    </TableCell>
                  {:else if headerKey === "Work_ResultEndTime"}
                    <TableCell>
                      {#if operation["Work_ResultEndTime"] !== "" || getFixedProperty(operation, "Status") === "B"}
                        <div style="color:#249E8E;" class="result-time-text">
                          {formatDateTimeFromDate(new Date(operation["Work_ResultEndTime"]))}
                        </div>
                      {:else}
                        <GeneralButton
                          buttonDisabled={!isInputAllowed ||
                            operation["Work_ResultStartTime"] === ""}
                          buttonFunction={(e) =>
                            onStartEndButtonClick(e, operation, "end")}
                          buttonText={translate("generic.finishShort")}
                          buttonExtraStyle="padding: 0; margin: 0; width: 100%; min-height: 50px"
                        />
                      {/if}
                    </TableCell>
                  {:else if headerKey === "Work_Status" && "NAIDTB".includes(operation[headerKey])}
                    <TableCell>{GetStatusText(GetStatus(operation[headerKey]))}</TableCell>
                  {:else if enumPropertiesDict[headerKey]}
                    <TableCell>{getAliasOrDisplayStringForEnumValue(enumPropertiesDict[headerKey], operation[headerKey])}</TableCell>
                  {:else if headerKey === "Work_ResultObtainTime"}
                    <TableCell>{formatDateTimeFromDate(new Date(operation["Work_ResultObtainTime"]))}</TableCell>
                  {:else}
                    <td>{operation[headerKey]}</td>
                  {/if}
                {/each}
                {#if $enableResultEdit && isInputAllowed}
                  <TableCell stickToRight={true} isResultEditButton={true} resultEditButtonOnClicked={() => editResultOnClick(operation)}/>
                {/if}
              </TableRow>
            {/each}
          </TableBody>
        </Table>
      {:else}
        <p>{translate("frontend:views.operationTable.noOperation")}</p>
      {/if}
    {:else}
      <Loading />
    {/if}
  </div>
</div>

{#if showResultEdit && operationToEditResult}
  <ResultEdit
    operationCode={getFixedProperty(operationToEditResult, "OperationCode")}
    prevStartDateTime={getFixedProperty(operationToEditResult, "ResultStartTime")}
    prevEndDateTime={getFixedProperty(operationToEditResult, "ResultEndTime")}
    prevStatus={getFixedProperty(operationToEditResult, "Status")}
    prevQty={getFixedProperty(operationToEditResult, "ResultQty")}
    itemQty={getFixedProperty(operationToEditResult, "OutputQty")}
    prevProgress={getFixedProperty(operationToEditResult, "ResultProgress")}
    prevObtainTime={getFixedProperty(operationToEditResult, "ResultObtainTime")}
    scheduledStartDateObject={new Date(getFixedProperty(operationToEditResult, "StartTime"))}
    scheduledEndDateObject={new Date(getFixedProperty(operationToEditResult, "EndTime"))}
    onSubmit={editResultOnSubmit}
    onClose={()=>showResultEdit = false}
  />
{/if}

<style>
  .content-root {
    height: 100%;
    max-height: 100%;
    display: flex;
    flex-direction: column;
  }
  .table-wrapper {
    display: flex;
    justify-content: center;
    background-color: white;
    height: 100%;
    overflow-y: auto;
    max-width: 100vw;
  }
  td {
    border: 1px solid #d3d3d3;
  }
</style>
